import { useMemo, useState } from "react";
import { hotRanking } from "@/mocks/siteData";
import SectionHeader from "./SectionHeader";

interface Props {
  query: string;
}

export default function HotRanking({ query }: Props) {
  const [showAll, setShowAll] = useState(false);
  const [kw, setKw] = useState("");

  const filtered = useMemo(() => {
    const q = `${kw} ${query}`.trim().toLowerCase();
    const base = q
      ? hotRanking.filter((it) => it.title.toLowerCase().includes(q) || it.tag.includes(q))
      : hotRanking;
    return base;
  }, [kw, query]);

  const visible = showAll ? filtered : filtered.slice(0, 5);
  const top = filtered[0];

  const trendIcon = (t: string) =>
    t === "up"
      ? "ri-arrow-up-line text-primary-500"
      : t === "down"
      ? "ri-arrow-down-line text-foreground-300"
      : "ri-arrow-right-line text-foreground-300";

  return (
    <section id="hot" className="relative scroll-mt-20 py-14 md:py-20 bg-background-50">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <SectionHeader eyebrow="今日热榜" title="今天大家都在看什么" desc="实时聚合人气话题，按热度智能排序，帮助你在信息洪流中快速锁定值得关注的焦点。" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          {/* Featured card */}
          {top && (
            <div className="lg:col-span-5">
              <article className="h-full rounded-2xl overflow-hidden border border-background-200 bg-background-100 flex flex-col">
                <div className="relative">
                  <div className="w-full h-52 md:h-64 bg-gradient-to-br from-primary-500 to-accent-500 relative flex items-center justify-center">
                    <span className="absolute inset-0 flex items-center justify-center font-heading font-black text-background-50/20 text-[160px] md:text-[200px] leading-none select-none">
                      {top.rank}
                    </span>
                    <div className="relative text-center px-6">
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-background-50/90 text-primary-700 text-xs font-semibold">
                        <i className="ri-fire-fill text-accent-600"></i> 榜首话题
                      </span>
                    </div>
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="px-2 py-0.5 rounded-md bg-accent-100 text-accent-800 font-semibold">#{top.tag}</span>
                    <span className="text-foreground-400">热度 {top.heatLabel}</span>
                  </div>
                  <h3 className="mt-3 font-heading font-bold text-xl md:text-2xl text-foreground-950 leading-snug">{top.title}</h3>
                  <p className="mt-3 text-foreground-600 text-sm leading-relaxed">
                    想了解这条话题为何高居榜首？我们从讨论热度、人群画像与衍生分支三个维度做了拆解，帮助你看懂它持续走红的原因，并延伸出更多值得关注的子话题。
                  </p>
                  <a
                    href="#trend"
                    className="mt-auto pt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 cursor-pointer"
                  >
                    进入热点深度解读 <i className="ri-arrow-right-line text-base w-4 h-4 flex items-center justify-center"></i>
                  </a>
                </div>
              </article>
            </div>
          )}

          {/* Ranking list */}
          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-background-200 bg-background-100 p-5 md:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                <div className="flex items-center gap-2">
                  <h3 className="font-heading font-bold text-lg text-foreground-950">人气排行</h3>
                  <span className="text-xs text-foreground-400">热度每分钟更新</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-background-50 border border-background-200 focus-within:border-primary-300 w-full sm:w-56">
                  <i className="ri-search-line text-foreground-400 text-sm w-4 h-4 flex items-center justify-center"></i>
                  <input
                    value={kw}
                    onChange={(e) => setKw(e.target.value)}
                    placeholder="筛选热榜"
                    className="flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                  />
                </div>
              </div>

              <ol className="divide-y divide-background-200">
                {visible.map((it, i) => (
                  <li key={it.rank} className="group flex items-center gap-3 py-3.5 cursor-pointer">
                    <span
                      className={`flex items-center justify-center w-8 h-8 rounded-lg font-heading font-black text-sm shrink-0 transition-colors ${
                        i === 0
                          ? "bg-primary-500 text-background-50"
                          : i === 1
                          ? "bg-accent-500 text-primary-950"
                          : i === 2
                          ? "bg-secondary-200 text-secondary-900"
                          : "bg-background-200 text-foreground-600"
                      }`}
                    >
                      {it.rank}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-sm md:text-[15px] text-foreground-900 group-hover:text-primary-600 transition-colors font-medium">
                        {it.title}
                      </p>
                      <span className="inline-flex items-center gap-2 text-[11px] text-foreground-400 mt-1">
                        <span className="px-1.5 py-px rounded bg-secondary-100 text-secondary-800">{it.tag}</span>
                        {it.heatLabel} 热度
                      </span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <i className={`${trendIcon(it.trend)} text-base w-4 h-4 flex items-center justify-center`}></i>
                      <span className="text-xs text-foreground-500 hidden sm:inline">{it.heat}</span>
                    </div>
                  </li>
                ))}
                {visible.length === 0 && (
                  <li className="py-12 text-center text-sm text-foreground-500">没有匹配“{kw}”的话题</li>
                )}
              </ol>

              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className="mt-4 w-full py-3 rounded-lg border border-background-300 text-sm font-semibold text-foreground-700 hover:bg-background-50 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
              >
                {showAll ? "收起列表" : `展开完整热榜（共 ${filtered.length} 条）`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}